/**
 * Typed TanStack Query v5 hooks over the Watchtower Console read API.
 *
 * The interfaces here mirror the Lambdas verbatim — they ARE the contract:
 *   - lib/lambdas/query.ts   (runsList / eventsDrillIn / logsDrillIn)
 *   - lib/lambdas/budgets.ts (budget rules + the alert feed)
 *
 * Usage data is READ-ONLY; budget rules are the only thing the console mutates.
 *
 * Routes:
 *   GET /v1/runs?from=&to=[&producer=][&<labelKey>=<val>…]  → { runs }
 *   GET /v1/runs/{producer}/{session_id}/events            → { events }
 *   GET /v1/runs/{producer}/{session_id}/logs?severity=&limit=&cursor=
 *                                                          → { logs, next_cursor }
 *   GET /v1/runs/{producer}/{session_id}/trace             → the AgentTrace, top-level
 *
 * A run is ADDRESSED by its producer — the Guardius `identity_id` that sent it
 * (`0x…` or `<type>-<uuid>`) — and never by a platform. A platform is only ever
 * something a record CLAIMED (`claimed_platforms` on a run, `claimed_platform`
 * on an event or log record): it may be shown as a label, and it never goes into
 * a URL or a query key, because no route serves it. `?platform=` on GET
 * /v1/runs is answered `400 platform_retired`.
 */

import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './auth';
import { ApiError, makeApi, type Api } from './api';

// ---------------------------------------------------------------------------
// Response shapes — mirror lib/lambdas/query.ts verbatim.
// ---------------------------------------------------------------------------

/** A per-run rollup from GET /v1/runs (query.ts runsList). `models` is the set
 *  of opaque model identifiers seen across the run's turns. `labels` are opaque
 *  correlation labels the service never interprets. */
export interface Run {
  session_id?: string;
  /** The run's address. Optional because a rollup written before the producer
   *  re-key comes back without one; such a run has no drill-in route and the
   *  console must not offer one. */
  producer?: string;
  /** Every platform the run's records CLAIMED, sorted; ALWAYS present, `[]`
   *  when none did. A label only — never an address. */
  claimed_platforms: string[];
  run_date: string;
  models: string[];
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  /** Derived at READ time from the versioned catalog + the caller's overrides —
   *  never a stored value. */
  cost_usd: number;
  /** Turns whose model resolved to no rate at all. Their tokens contribute
   *  NOTHING to `cost_usd`, so a run with `unpriced_turns > 0` is under-costed
   *  and must say so — a bare `$0.0000` would read as a real price. */
  unpriced_turns?: number;
  /** The distinct models behind `unpriced_turns`, sorted. */
  unpriced_models?: string[];
  turn_count: number;
  log_count?: number;
  error_count?: number;
  first_seen?: string;
  last_seen?: string;
  env?: string;
  labels: Record<string, string>;
}

export interface RunsResponse {
  runs: Run[];
}

/** A per-turn EVENT row from GET /v1/runs/{producer}/{session_id}/events
 *  (query.ts eventsDrillIn), ascending by SK. */
export interface TurnEvent {
  sk: string;
  session_id: string;
  producer: string;
  /** What this record claimed, when it claimed anything. A label only. */
  claimed_platform?: string;
  model: string;
  env: string;
  ts_iso: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  /** DERIVED at read time from tokens x the rate in force, not the stored value. */
  cost_usd: number;
  /**
   * true when no rate resolved AND the producer reported no cost, so `cost_usd`
   * above is a placeholder zero rather than a price. Optional because a cached
   * console can briefly see a response predating the field.
   */
  unpriced?: boolean;
}

export interface EventsResponse {
  events: TurnEvent[];
}

/** A general (non-api_request) log record from GET
 *  /v1/runs/{producer}/{session_id}/logs (query.ts logsDrillIn). `body`/`attrs`
 *  are opaque; the `*_truncated` flags mark values the receiver clipped. */
export interface LogLine {
  ts_iso: string;
  severity: string;
  body: unknown;
  event_name?: string;
  attrs?: Record<string, unknown>;
  session_id: string;
  /** The attested sender (a Guardius identity_id). The address is the URL's. */
  identity_id: string;
  /** What this record claimed, when it claimed anything. A label only. */
  claimed_platform?: string;
  claimed_subject?: string;
  wallet_address?: string;
  actor_agent_ref?: string;
  env?: string;
  seq: number;
  body_truncated?: boolean;
  attrs_truncated?: boolean;
}

export interface LogsResponse {
  logs: LogLine[];
  /** Opaque base64url cursor for the next page, or null when exhausted. */
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// Agent trace — mirrors `lib/lambdas/shared/agent-trace.ts` BY HAND (issue
// #155, spec §5). `dashboard/` does not import from `lib/`, so these are a
// re-declaration and they inherit NONE of that module's guards: in particular
// `boolAttr` / `intAttr` are not available here, which is why `TraceMarker.attrs`
// is rendered as text and never coerced (see TracePanel).
//
// Every field of the response is ALWAYS PRESENT; unknown scalars are `null`,
// never omitted, and `summary` is all zeros on an empty trace. Nothing here is
// optional, and no consumer may branch on presence (spec §5).
// ---------------------------------------------------------------------------

/**
 * A CLOSED 3-member union — mirrors `SpanState`. Switches on it use an
 * exhaustive `switch` with `const _exhaustive: never`, because a hand-mirrored
 * union that silently widens is how this repo rendered `undefined undefined`
 * twice with `tsc` clean.
 *
 * `unterminated` means NO RESULT RECORD REACHED WATCHTOWER. It is never
 * evidence about the agent: our own `LOG_ATTRS_MAX` attribute cap, the
 * receiver's 256-records-per-request cap, the INFO severity floor and this
 * plane's own TWO bounds (`TRACE_MAX_RECORDS` and `TRACE_MAX_RESPONSE_BYTES`)
 * can each manufacture one (spec §4.6) — FIVE causes, not four: the byte budget
 * cuts the tail in total order just as the record cap does.
 */
export type SpanState = 'complete' | 'unterminated' | 'orphan_result';

/** The MAPPED marker events — mirrors `MarkerEventName` (keys of
 *  `MARKER_ATTR_ALLOWLIST`, spec §4.3). Event names on the wire are an OPEN set
 *  from an untrusted producer, so a renderer keyed on this union needs a typed
 *  `default`, never a `never` arm, for a name outside it. */
export type MarkerEventName =
  | 'mcp_server_connection'
  | 'hook_execution_complete'
  | 'hook_execution_start'
  | 'plugin_loaded'
  | 'assistant_response'
  | 'user_prompt'
  | 'hook_registered'
  | 'managed_settings_resolved'
  | 'retention_sweep';

/** One tool call — a `tool_decision` joined to its `tool_result` (spec §4.2). */
export interface TraceSpan {
  tool_use_id: string | null;
  tool_name: string | null;
  decision: string | null;
  source: string | null;
  tool_source: string | null;
  prompt_id: string | null;
  sequence: number | null;
  state: SpanState;
  /** `null` is UNKNOWN, never false. `!span.ok` would report every unknown as a
   *  failure — the fabricated-failure trap of spec §4.5 re-entering through the
   *  mirror. Test for `ok === false`. */
  ok: boolean | null;
  error_type: string | null;
  duration_ms: number | null;
  input_bytes: number | null;
  output_bytes: number | null;
  decided_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  attrs_truncated: boolean;
}

/** A lifecycle event (spec §4.3). `attrs` holds only allowlisted scalars that
 *  were actually present — and they are VERBATIM WIRE SCALARS: `is_plugin`
 *  arrives as the STRING `'false'` and `duration_ms` as `'70'` on the JSON wire.
 *  `Boolean('false')` is `true`, so these are rendered as text and never
 *  coerced here. */
export interface TraceMarker {
  event: string;
  /** `null` when the stored record had no readable `ts_iso` — the server sends
   *  the key either way (spec §5), so this is the only shape to handle. */
  at: string | null;
  sequence: number | null;
  prompt_id: string | null;
  attrs: Record<string, string | number | boolean>;
  attrs_truncated: boolean;
}

/** Zeros, never nulls (spec §4.4). `tool_busy_ratio` is a FRACTION (0.048 on
 *  the measured session), not a percentage. */
export interface TraceSummary {
  session_span_ms: number;
  tool_busy_ms: number;
  tool_busy_ratio: number;
  tool_call_count: number;
  failure_count: number;
  unterminated_count: number;
  truncated_records: number;
}

/** The whole response body of GET …/trace — top level, no envelope. */
export interface AgentTrace {
  schema_version: number;
  /**
   * WHO emitted this session, hand-mirrored from `AgentTrace['producer']` in
   * `lib/lambdas/shared/agent-trace.ts` — the authority for what each member
   * means and for the quantifiers behind it; do not restate them here.
   * `TracePanel`'s `producerLabel` reads this behind an exhaustive `switch`, and
   * `test/trace-console-mirror.test.ts` (root suite, the only place that can see
   * both sides) pins this union against the server's in both directions.
   *
   * It reports what the records CLAIMED. Nothing authenticates a producer from
   * its own attributes, so no copy built on this field may imply either label is
   * verified (spec §7 of the lifecycle-emit-contract design).
   */
  producer: 'claude_code' | 'conformant' | null;
  truncated: boolean;
  unrecognized_events: string[];
  summary: TraceSummary;
  spans: TraceSpan[];
  markers: TraceMarker[];
}

// ---------------------------------------------------------------------------
// Api context — built from useAuth().
// ---------------------------------------------------------------------------

const ApiContext = createContext<Api | null>(null);

/** Exported ONLY so tests can mount a hook with a stub Api; production code uses
 *  <ApiProvider/> + useApi(). */
export const ApiContextForTests = ApiContext;

/** Provides the Api instance, wired to the auth layer's `ensureFreshToken`.
 *  The client awaits that getter before every request, so each request carries
 *  the BEST token available: one valid for at least EXPIRY_SKEW_MS where it can
 *  get one — refreshing silently if the current one is missing or about to
 *  expire — else an access token that is merely still UNEXPIRED (#194 delta 4
 *  deliberately spends the in-flight margin on that rung, for the v2.1 SOFT
 *  END). When the getter answers `null` no request is sent at all — and that
 *  `null` means "no usable token right now", never "signed out":
 *  `makeApi` rejects before `fetch` with a plain Error, deliberately NOT an
 *  ApiError, because an empty bearer earns a 401 and the 401 branch below signs
 *  the user out — which after a rate-limited refresh (#188) would discard a
 *  refresh token that is still good. */
export function ApiProvider({ children }: { children: ReactNode }) {
  const { config, ensureFreshToken, signOut, denyAccess } = useAuth();
  const api = useMemo<Api>(
    // `ensureFreshToken` is re-created every AuthProvider render, so this
    // rebuilds `makeApi` each render — cheap, and it guarantees we never capture
    // a stale closure. The getter itself reads live sessionStorage, so a rebuilt
    // instance is always correct.
    //
    // Wrap every verb so the spec §5 security contract holds. The error is
    // always rethrown so react-query still surfaces the failure downstream.
    () => {
      const base = makeApi(config, ensureFreshToken);
      // A 401 on ANY verb means the session is dead → sign out.
      // A 403 latches NoAccess for READS ONLY (spec §5): a reader-only user
      // POSTing a budget rule must see an inline form error, not a blanked
      // console.
      const onError = (e: unknown, latchDenied: boolean) => {
        if (e instanceof ApiError) {
          if (e.status === 401) signOut();
          else if (e.status === 403 && latchDenied) denyAccess();
        }
        throw e;
      };
      return {
        get: async <T,>(path: string): Promise<T> => {
          try { return await base.get<T>(path); } catch (e) { return onError(e, true) as never; }
        },
        post: async <T,>(path: string, body: unknown): Promise<T> => {
          try { return await base.post<T>(path, body); } catch (e) { return onError(e, false) as never; }
        },
        patch: async <T,>(path: string, body: unknown): Promise<T> => {
          try { return await base.patch<T>(path, body); } catch (e) { return onError(e, false) as never; }
        },
        del: async (path: string): Promise<void> => {
          try { await base.del(path); } catch (e) { onError(e, false); }
        },
      };
    },
    [config, ensureFreshToken, signOut, denyAccess],
  );
  return createElement(ApiContext.Provider, { value: api }, children);
}

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used within an ApiProvider');
  return api;
}

// ---------------------------------------------------------------------------
// Read hooks.
// ---------------------------------------------------------------------------

/** GET /v1/runs?from=&to=[&producer=][&k=v…] → per-run rollups over [from, to]
 *  (inclusive), narrowed by an optional producer and opaque label filters.
 *  Gated by `enabled` so nothing fires until both date bounds are set. */
export function useRuns(
  from: string,
  to: string,
  producer?: string,
  labelFilters?: Record<string, string>,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['runs', from, to, producer ?? '', labelFilters ?? {}] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('from', from);
      params.set('to', to);
      if (producer) params.set('producer', producer);
      for (const [k, v] of Object.entries(labelFilters ?? {})) params.set(k, v);
      return api.get<RunsResponse>(`/v1/runs?${params.toString()}`);
    },
    enabled: Boolean(from && to),
  });
}

/** GET /v1/runs/{producer}/{session_id}/events → the per-turn EVENT drill-in
 *  for one run, ascending by SK.
 *
 *  Every drill-in query key names the PRODUCER: a session id is unique only
 *  within its producer, so a key without it would serve one producer's rows for
 *  another's session of the same id, from cache, with no request to show it. */
export function useTurns(producer: string, sessionId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['events', producer, sessionId] as const,
    queryFn: () =>
      api.get<EventsResponse>(
        `/v1/runs/${encodeURIComponent(producer)}/${encodeURIComponent(sessionId)}/events`,
      ),
    enabled: Boolean(producer && sessionId),
  });
}

/** GET /v1/runs/{producer}/{session_id}/logs?severity=&limit=200[&cursor=] →
 *  the general-log drill-in for one run, paged. `severity` is a FLOOR filter.
 *  `cursor` is the opaque next_cursor returned by the previous page. */
export function useLogs(producer: string, sessionId: string, severity: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['logs', producer, sessionId, severity] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('severity', severity);
      params.set('limit', '200');
      if (pageParam) params.set('cursor', pageParam);
      return api.get<LogsResponse>(
        `/v1/runs/${encodeURIComponent(producer)}/${encodeURIComponent(sessionId)}/logs?${params.toString()}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(producer && sessionId),
  });
}

/**
 * GET /v1/runs/{producer}/{session_id}/trace → the derived agent trace.
 *
 * `enabled` is a REQUIRED caller-supplied gate, not a convenience: the route
 * does not paginate and derives over the whole session, so one call lists the
 * entire session prefix and GETs every object sequentially inside a single
 * Lambda invocation. An eager mount would make every run drill-in pay that, so
 * `TracePanel` passes `false` until the section is first expanded (spec §6).
 */
export function useTrace(producer: string, sessionId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['trace', producer, sessionId] as const,
    queryFn: () =>
      api.get<AgentTrace>(
        `/v1/runs/${encodeURIComponent(producer)}/${encodeURIComponent(sessionId)}/trace`,
      ),
    enabled: enabled && Boolean(producer && sessionId),
  });
}

// ---------------------------------------------------------------------------
// Budget rules + alerts — mirror lib/lambdas/budgets.ts verbatim.
//
//   POST   /v1/budgets            → the created rule (201)
//   GET    /v1/budgets            → { budgets }
//   PATCH  /v1/budgets/{rule_id}  → the updated rule
//   DELETE /v1/budgets/{rule_id}  → 204
//   GET    /v1/alerts?limit=&cursor= → { alerts, next_cursor }
// ---------------------------------------------------------------------------

export type BudgetKind = 'run' | 'period' | 'cadence';
export type BudgetMetric = 'cost_usd' | 'total_tokens' | 'turn_count' | 'silence_minutes';
export type BudgetPeriod = 'day' | 'month';

/**
 * Which clock a cadence alert measured its silence FROM (ingest-cadence spec
 * §3.1 step 4 / §4). Mirrors `SilenceBasis` in
 * `lib/lambdas/shared/budget-rules.ts`.
 *
 * It is what stops `observed_value` being read as a precise outage length when
 * it is a lower bound:
 *
 * - `last_seen` — a frontier was observed; the value IS the outage length.
 * - `rule_created_at` — nothing was ever observed and the rule is younger than
 *   the window; the value is silence *since this rule began watching*.
 * - `beyond_window` — the last data point, if any, is older than anything the
 *   evaluator can still see; the value is a LOWER BOUND. This basis, and only
 *   this basis, is rendered "at least N" (the webhook's `silenceQualifier`
 *   makes the same distinction in the same words, for the same reason).
 */
export type SilenceBasis = 'last_seen' | 'rule_created_at' | 'beyond_window';

/**
 * Bounds on a `cadence` rule's `threshold`, in MINUTES — mirrored from
 * `CADENCE_THRESHOLD_MIN_MINUTES` / `_MAX_MINUTES` in
 * `lib/lambdas/shared/budget-rules.ts` (spec §2.2), so the form can state them
 * to the user and reject out-of-range input before the round trip. The server
 * stays authoritative; its `400 {error: 'invalid_field', field: 'threshold'}`
 * is still surfaced inline.
 *
 * The floor exists because detection RESOLUTION is bounded by the hourly
 * evaluation pass, not by the threshold; the ceiling because the observation
 * window is derived from the threshold.
 */
export const CADENCE_THRESHOLD_MIN_MINUTES = 60;
export const CADENCE_THRESHOLD_MAX_MINUTES = 20160;

/** A rule as the API returns it. `webhook_secret` is NEVER present — the server
 *  substitutes the boolean `webhook_secret_set`. */
export interface BudgetRuleView {
  rule_id: string;
  name: string;
  kind: BudgetKind;
  metric: BudgetMetric;
  threshold: number;
  period?: BudgetPeriod;
  /** The one producer the rule reads; absent = every producer of the owner. */
  scope_producer?: string;
  /**
   * RETIRED, READ-ONLY. A rule stored before the producer cutover may still
   * carry this, and the GET returns it as stored. The evaluator DROPS such a
   * rule on every pass (it is not evaluated at all), and neither create nor
   * patch accepts the field — not even `null` — so the console renders it as
   * retired and never sends it.
   */
  scope_platform?: string;
  webhook_url?: string;
  webhook_secret_set: boolean;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BudgetsResponse {
  budgets: BudgetRuleView[];
}

/** A fired alert. `sk` is the stable row key — use it as the React list key. */
export interface AlertView {
  sk: string;
  rule_id: string;
  rule_name: string;
  kind: BudgetKind;
  metric: BudgetMetric;
  threshold: number;
  observed_value: number;
  scope_producer?: string;
  /** Run alerts only: the run's `(producer, session_id)`. */
  session_id?: string;
  producer?: string;
  period?: BudgetPeriod;
  period_key?: string;
  /** Cadence alerts only (spec §4); `session_id` / `producer` / `period` /
   *  `period_key` are absent on those rows, and nothing in the console may
   *  render their `undefined`. `silence_since` is the clock ISO the silence was
   *  measured from and `silence_basis` says what that clock MEANS — see
   *  `SilenceBasis`. Both optional because one row shape serves three kinds. */
  silence_since?: string;
  silence_basis?: SilenceBasis;
  fired_at: string;
  /** `pending` means the evaluator wrote the row and then died before its POST
   *  resolved — a webhook WAS configured, so it must never render as "no
   *  webhook". Mirrors `AlertRow.delivery` in budget-rules.ts. */
  delivery: 'none' | 'pending' | 'ok' | 'failed';
  delivery_error?: string;
}

export interface AlertsResponse {
  alerts: AlertView[];
  next_cursor: string | null;
}

export interface BudgetCreateInput {
  name: string;
  kind: BudgetKind;
  metric: BudgetMetric;
  threshold: number;
  period?: BudgetPeriod;
  scope_producer?: string;
  webhook_url?: string;
  webhook_secret?: string;
  enabled?: boolean;
}

/**
 * kind / metric / period are immutable server-side (spec §6.3) and absent here.
 *
 * `null` on `scope_producer` / `webhook_url` CLEARS the stored value — the only
 * way to remove a webhook without delete-and-recreate (which would mint a new
 * `rule_id`, hence new dedup keys, and re-POST every crossing still in the
 * lookback). `""` is rejected server-side; `null` is the sentinel.
 */
export interface BudgetPatchInput {
  name?: string;
  threshold?: number;
  scope_producer?: string | null;
  webhook_url?: string | null;
  webhook_secret?: string;
  enabled?: boolean;
}

const ALERTS_PAGE_SIZE = 100;

/** GET /v1/budgets → the caller's own rules. */
export function useBudgets() {
  const api = useApi();
  return useQuery({
    queryKey: ['budgets'] as const,
    queryFn: () => api.get<BudgetsResponse>('/v1/budgets'),
  });
}

/** POST /v1/budgets. Invalidates the rules list on success. */
export function useCreateBudget() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: BudgetCreateInput) => api.post<BudgetRuleView>('/v1/budgets', input),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['budgets'] }); },
  });
}

/** PATCH /v1/budgets/{rule_id} — also the enable/disable toggle. */
export function useUpdateBudget() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, patch }: { ruleId: string; patch: BudgetPatchInput }) =>
      api.patch<BudgetRuleView>(`/v1/budgets/${encodeURIComponent(ruleId)}`, patch),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['budgets'] }); },
  });
}

/** DELETE /v1/budgets/{rule_id}. Alert rows are NOT cascaded server-side. */
export function useDeleteBudget() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.del(`/v1/budgets/${encodeURIComponent(ruleId)}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['budgets'] }); },
  });
}

/** GET /v1/alerts?limit=100[&cursor=] → the reverse-chronological alert feed. */
export function useAlerts() {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['alerts'] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('limit', String(ALERTS_PAGE_SIZE));
      if (pageParam) params.set('cursor', pageParam);
      return api.get<AlertsResponse>(`/v1/alerts?${params.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

// ---------------------------------------------------------------------------
// Per-owner model price overrides — mirror lib/lambdas/prices.ts verbatim.
//
//   GET    /v1/prices              → { prices }
//   POST   /v1/prices              → the stored override (201)
//   DELETE /v1/prices?model=<enc>  → 204
//
// The console NEVER prices anything: it does not bundle the rate catalog and
// does not compute a cost. An override is an INPUT the server applies at read
// time; every `cost_usd` the console renders arrived already computed.
// ---------------------------------------------------------------------------

/** One model's four rates, in USD per token. A zero rate is ABSENT, not free —
 *  the server rejects an all-zero override (`all_rates_zero`). */
export interface PriceOverride {
  model: string;
  input_rate: number;
  output_rate: number;
  cache_read_rate: number;
  cache_write_rate: number;
  created_at?: string;
  updated_at?: string;
  actor_agent_ref?: string;
}

export interface PricesResponse {
  prices: PriceOverride[];
}

/** POST body: the model plus all four rates. A price is REPLACED whole, never
 *  patched (the API rejects PATCH outright), so every rate is required. */
export type PriceUpsertInput = Pick<
  PriceOverride,
  'model' | 'input_rate' | 'output_rate' | 'cache_read_rate' | 'cache_write_rate'
>;

/** GET /v1/prices → the caller's own overrides. */
export function usePrices() {
  const api = useApi();
  return useQuery({
    queryKey: ['prices'] as const,
    queryFn: () => api.get<PricesResponse>('/v1/prices'),
  });
}

/** POST /v1/prices — upsert one model's rates. Invalidates the override list
 *  AND every runs/events query: a new rate changes what those reads compute. */
export function useUpsertPrice() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PriceUpsertInput) => api.post<PriceOverride>('/v1/prices', input),
    onSuccess: () => { invalidatePriced(client); },
  });
}

/** DELETE /v1/prices?model=… — the model rides the QUERY STRING, never the
 *  path: model identifiers contain '/', and percent-encoded slashes in path
 *  parameters are unreliable through API Gateway. */
export function useDeletePrice() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (model: string) => api.del(`/v1/prices?model=${encodeURIComponent(model)}`),
    onSuccess: () => { invalidatePriced(client); },
  });
}

/** Costs are derived at read time, so any override change invalidates the price
 *  list and everything priced from it. */
function invalidatePriced(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['prices'] });
  void client.invalidateQueries({ queryKey: ['runs'] });
  void client.invalidateQueries({ queryKey: ['events'] });
}

// ---------------------------------------------------------------------------
// Audit packages (audit-ingest spec §7) — READ ONLY from the console.
//
//   GET /v1/audit[?platform=&agent=&outcome=&since=&until=]  → { audits, cursor? }
//   GET /v1/audit/{package_id}                               → { envelope, package }
//
// The list rows are PROJECTED envelopes: the package body is never in the list
// response, only in the detail fetch. The console never interprets a package —
// it renders the envelope fields and the body as pretty-printed JSON.
// ---------------------------------------------------------------------------

/** One stored package's envelope, as the list/detail routes project it. */
export interface AuditEnvelopeView {
  package_id: string;
  run_id: string;
  platform: string;
  agent: string;
  occurred_at: string;
  outcome: string;
  content_digest: string;
  envelope_version?: number;
  actor_agent_ref?: string;
  stored_at: string;
  size_bytes: number;
}

/** Envelope-level list filters. Empty values are dropped from the query string
 *  rather than sent as '' — an empty `since` is unparseable and 400s. */
export interface AuditFilters {
  platform?: string;
  agent?: string;
  outcome?: string;
  since?: string;
  until?: string;
}

export interface AuditListResponse {
  audits: AuditEnvelopeView[];
  /** Present whenever the store has more to scan. Because the server filters
   *  AFTER reading, a SHORT page can still carry a cursor — a short page is
   *  never proof that these are all the results. */
  cursor?: string;
}

export interface AuditDetailResponse {
  envelope: AuditEnvelopeView;
  /** The package body, opaque. The console pretty-prints it and interprets
   *  nothing inside it. */
  package: unknown;
}

/** GET /v1/audit → the caller's own stored audit packages, envelope-projected.
 *
 *  `cursor` requests the page AFTER the one that returned it. It is a separate
 *  parameter rather than an `AuditFilters` member because it is not a filter:
 *  it is a position, and a caller must never be able to "filter by cursor". It
 *  is part of the query key, so each page caches independently. */
export function useAudits(filters: AuditFilters, cursor?: string) {
  const api = useApi();
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => !!v) as [string, string][],
  );
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  return useQuery({
    queryKey: ['audits', filters, cursor ?? ''] as const,
    queryFn: () => api.get<AuditListResponse>(`/v1/audit${qs ? `?${qs}` : ''}`),
  });
}

/** GET /v1/audit/{package_id} → one package, envelope + body. Disabled until a
 *  package is selected, so opening the tab fetches no bodies. */
export function useAuditDetail(packageId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['audit', packageId] as const,
    enabled: packageId !== null,
    queryFn: () =>
      api.get<AuditDetailResponse>(`/v1/audit/${encodeURIComponent(packageId!)}`),
  });
}

// ---------------------------------------------------------------------------
// Root provider: QueryClientProvider + ApiProvider.
// ---------------------------------------------------------------------------

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
    },
  });
}

export function QueryProvider({
  client,
  children,
}: {
  client: QueryClient;
  children: ReactNode;
}) {
  return createElement(
    QueryClientProvider,
    { client },
    createElement(ApiProvider, null, children),
  );
}
